import {
  computed,
  defineComponent,
  ref,
  onBeforeUnmount,
  watch,
  useModel,
  type PropType,
} from 'vue'
import { useCountryStore, storeToRefs } from '@/store'
import { HDropdown, SvgIcon, CurrencyFlag, type DropdownState } from '@/components'
import { debounce } from 'lodash'
import { Spin } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import type { CountryItem } from '@/store/country/types'

export default defineComponent({
  name: 'CountrySelector',
  inheritAttrs: false,
  props: {
    placeholder: {
      type: String,
    },
    mode: {
      type: String as () => 'country' | 'phone',
      default: 'country',
    },
    overlayClass: {
      type: String,
      default: 'bg-ground-50',
    },
    containerClass: {
      type: String,
      default: '',
    },
    bordered: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    selectClass: {
      type: String,
      default: 'select',
    },
    modelValue: {
      type: String,
      default: undefined,
    },
    value: {
      type: String,
      default: undefined,
    },
    countryEnName: {
      type: String,
      default: undefined,
    },
    countryDisplayName: {
      type: String,
      default: undefined,
    },
    phoneCode: {
      type: [String, Number],
      default: undefined,
    },
    theme: {
      type: String,
      default: 'default',
    },
    overlayMatchTriggerWidth: {
      type: Boolean,
      default: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl'>,
      default: 'md',
      validator: (v): v is 'xs' | 'sm' | 'md' | 'lg' | 'xl' =>
        ['xs', 'sm', 'md', 'lg', 'xl'].includes(v as any),
    },
  },
  emits: [
    'update:modelValue',
    'update:value',
    'update:countryEnName',
    'update:countryDisplayName',
    'update:phoneCode',
    'change',
  ],
  setup(props, { emit, attrs }) {
    const { t } = useI18n()
    const countryStore = useCountryStore()
    const { i18nCountryList, loading: countryLoading, defaultCountry } = storeToRefs(countryStore)

    const didAutoSelect = ref(false)
    const isComposing = ref(false)
    const keyword = ref('')

    const updateKeywordNow = (v: string) => (keyword.value = v)
    const updateKeywordDebounced = debounce(updateKeywordNow, 250, {
      leading: false,
      trailing: true,
    })

    const filteredOptions = computed(() => {
      const raw = keyword.value.trim().toLowerCase()
      if (!raw) return i18nCountryList.value

      const list = i18nCountryList.value.filter(
        ({ countryEnName, countryCode, countryDisplayName, phoneCode }: CountryItem) => {
          const nameMatch = [countryEnName, countryDisplayName, countryCode, phoneCode].some(
            (item: string) =>
              String(item ?? '')
                .toLowerCase()
                .includes(raw),
          )

          return nameMatch
        },
      )
      return list
    })

    const handleSearch = (event: Event) => {
      const target = event.target as HTMLInputElement
      if (isComposing.value) return
      updateKeywordDebounced(target.value.trim())
    }

    const modelValue = useModel(props, 'modelValue')
    const value = useModel(props, 'value')
    const countryEnName = useModel(props, 'countryEnName')
    const countryDisplayName = useModel(props, 'countryDisplayName')
    const phoneCode = useModel(props, 'phoneCode', {
      // 把外部 number|string 读进来统一转成 string
      get: (v) => String(v ?? ''),
      // 写回去也统一成 string（避免 number/string 混用）
      set: (v) => String(v ?? ''),
    })

    const handleCountryChange = (country: CountryItem, callback: () => void) => {
      modelValue.value = country.countryCode
      value.value = country.countryCode
      countryEnName.value = country.countryEnName
      countryDisplayName.value = country.countryDisplayName
      phoneCode.value = country.phoneCode
      // Emit both the selected value (countryCode/phoneCode) and countryEnName for consumers who need it
      emit('change', country.countryCode)
      keyword.value = ''
      callback()
    }

    // 大小写不敏感、空安全比较
    const eq = (a?: string | number, b?: string | number) =>
      String(a ?? '').toUpperCase() === String(b ?? '').toUpperCase()

    // 根据任意输入去列表里找国家（code / phone / enName）
    const pickCountry = (needle?: string | number) => {
      const key = String(needle ?? '')
      if (!key) return undefined
      return i18nCountryList.value.find(
        (item: CountryItem) =>
          eq(item.countryCode, key) || eq(item.phoneCode, key) || eq(item.countryEnName, key),
      )
    }

    watch(
      [
        modelValue,
        value,
        countryEnName,
        () => (props.mode === 'phone' ? String(props.phoneCode ?? '') : ''),
        i18nCountryList,
      ],
      ([mv, v, en, pc]) => {
        // 取“最新的非空输入”（从左到右优先级：modelValue > value > countryEnName > phoneCode）
        const latest = [mv, v, en, pc].find((x) => String(x ?? '') !== '')
        if (!latest || !Array.isArray(i18nCountryList.value) || i18nCountryList.value.length === 0)
          return

        const country = pickCountry(latest)
        if (!country) return

        // 如果当前已是目标值，避免重复写入触发循环
        const alreadySynced =
          eq(modelValue.value, country.countryCode) &&
          eq(value.value, country.countryCode) &&
          eq(countryEnName.value, country.countryEnName) &&
          eq(phoneCode.value, country.phoneCode)

        if (alreadySynced) return
        handleCountryChange(country, () => {})
      },
      { immediate: true },
    )

    const handleToggle = (toggle: () => void) => {
      if (props.disabled || props.readonly) return
      keyword.value = ''
      toggle()
    }

    onBeforeUnmount(() => {
      updateKeywordDebounced.cancel?.()
    })

    // 当国家列表就绪且还没有外部值时，若存在 checked===1 的默认项则自动选中
    watch(
      [i18nCountryList, defaultCountry],
      ([list, newDefaultCountry]) => {
        if (didAutoSelect.value) return
        // 外部已经传了值就不覆盖（null/undefined/空串都视为“无值”）
        const hasExternal =
          (props.modelValue ?? '') !== '' ||
          (props.value ?? '') !== '' ||
          (props.countryEnName ?? '') !== '' ||
          (props.countryDisplayName ?? '') !== '' ||
          (props.phoneCode ?? '') !== ''

        if (props.readonly || props.disabled || hasExternal) return
        if (!Array.isArray(list) || list.length === 0) return
        if (!newDefaultCountry) return

        // 执行一次默认选中（不会展开/关闭下拉）
        handleCountryChange(newDefaultCountry as CountryItem, () => {})
        didAutoSelect.value = true
      },
      { immediate: true },
    )

    const wraperClass = computed(() => {
      const sizeMap = {
        'px-1 py-2 rounded-sm text-xs h-[28px]': props.size === 'xs',
        'px-[5px] py-2 rounded-sm text-xs h-[30px]': props.size === 'sm',
        'px-2 py-[10px] rounded-lg text-sm h-[38px]': props.size === 'md',
        'px-2 py-3 rounded-[10px] text-sm h-[42px]': props.size === 'lg',
        'px-[13px] py-3 rounded-[10px] text-sm h-[48px]': props.size === 'xl',
      }

      const borderClass =
        (props.bordered ?? true)
          ? 'border border-rim-300 hover:border-ink-400 focus:border-blue-primary'
          : 'border-none'
      const bgClass = props.disabled || props.readonly ? 'bg-ground-50' : 'bg-transparent'
      const disabledClass =
        props.disabled || props.readonly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      return [sizeMap, borderClass, bgClass, disabledClass]
    })

    return () => (
      <div
        class={[
          props.fullWidth && 'w-full',
          'country-selector',
          `country-selector-${props.theme}`,
          `country-selector-${props.size}`,
          { 'country-selector-disabled': props.disabled },
          wraperClass.value,
          attrs.class,
        ]}
        style={attrs.style as any}
      >
        <HDropdown
          position="bottomLeft"
          overlay={({ close }: DropdownState) => (
            <div
              class="p-3 max-h-[240px] rounded-lg shadow-lg flex flex-col bg-indicator-model "
              style={props.overlayMatchTriggerWidth ? { width: 'var(--trigger-width)' } : undefined}
            >
              <input
                type="text"
                autocomplete="off"
                name="no-autofill-search"
                class={[
                  'flex-none h-8 text-sm rounded-md border px-2 mb-2 outline-none transition-colors',
                  'border-rim-300 focus:border-ink-400',
                  'placeholder:text-ink-500',
                ]}
                style={{ color: '#4B5563' }}
                placeholder={t('components.countrySelector.search')}
                onInput={handleSearch}
                onCompositionstart={() => (isComposing.value = true)}
                onCompositionend={(e) => {
                  isComposing.value = false
                  const val = (e.target as HTMLInputElement).value
                  updateKeywordDebounced.flush?.()
                  updateKeywordDebounced(val.trim())
                }}
              />
              <div
                class={[
                  'flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent',
                  'scrollbar-thumb-[#D1D5DB]',
                ]}
              >
                {filteredOptions.value?.map((country) => (
                  <div
                    key={country.key}
                    class={[
                      'h-10 px-2 py-1 cursor-pointer rounded-md transition-colors',
                      'hover:bg-ground-100',
                    ]}
                    onClick={() => {
                      if (props.readonly || props.disabled) return
                      handleCountryChange(country, close)
                    }}
                  >
                    <div class={'flex items-center gap-2 w-full h-full text-ink-900'}>
                      <CurrencyFlag flag={country.flag} size="md" shape="square" ratio="4x3" />
                      <div class={'flex-1 flex items-center justify-between'}>
                        <div class={'text-sm  truncate'}>{country.countryDisplayName}</div>
                        {props.mode === 'phone' && (
                          <div class={'text-sm font-mono'}>+{country.phoneCode}</div>
                        )}
                        {props.mode === 'country' && (
                          <div class={'text-sm font-mono'}>{country.countryCode}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        >
          {({ toggle }: DropdownState) => (
            <div
              class={['w-full h-full flex items-center gap-2']}
              onClick={() => handleToggle(toggle)}
            >
              {countryLoading.value ? (
                <div class={'w-full h-full flex items-center justify-center'}>
                  <Spin size="small" />
                </div>
              ) : (
                <div class={'w-full flex items-center justify-between gap-2'}>
                  {modelValue.value ? (
                    <div class={'flex items-center gap-2'}>
                      {/* 国家国旗 */}
                      <CurrencyFlag flag={modelValue.value} size="md" shape="square" ratio="4x3" />
                      <div class={'flex-1 flex items-center justify-between min-w-0'}>
                        {/* 国家名称 */}
                        <div class={['truncate text-sm font-medium']}>
                          {countryDisplayName.value}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div class={'text-ink-500'}>
                      {props.placeholder || t('components.countrySelector.placeholder')}
                    </div>
                  )}
                  {/* 下拉箭头 */}
                  <SvgIcon name="select-arrow" size="14" class="!fill-ink-500 flex-shrink-0 ml-2" />
                </div>
              )}
            </div>
          )}
        </HDropdown>
      </div>
    )
  },
})
